// Markdown → sanitized HTML. Used by the compose preview + the article
// reader (PostView) so what you type is what publishes.
//
// Rendering is done client-side for POC — the server stores raw markdown
// unchanged. When PH1-BE-01 (SEO) lands we'll switch to server-side render
// via goldmark + bluemonday so bots see rendered HTML too.

import DOMPurify from 'dompurify';
import { marked } from 'marked';

// marked's defaults are already GFM-flavoured. We turn off automatic
// mangling of email addresses and heading IDs (paranoid CSP) — visitors
// don't need to link inside a body they can't share yet.
marked.setOptions({
    gfm:       true,
    breaks:    true,   // single newline → <br> (feels like WhatsApp)
});

/** Parse + sanitize. Returns HTML safe to dangerouslySetInnerHTML. */
export function renderMarkdown(src: string): string {
    if (!src) return '';
    const raw  = marked.parse(src, { async: false }) as string;
    return DOMPurify.sanitize(raw, {
        // Whitelist that keeps prose + inline formatting + links, drops
        // everything scriptable. No <img> until per-user media policy is
        // sorted; markdown images should be posted via the media uploader.
        ALLOWED_TAGS: [
            'p', 'br', 'hr',
            'strong', 'em', 'del', 'code', 'pre',
            'a',
            'ul', 'ol', 'li',
            'blockquote',
            'h1', 'h2', 'h3', 'h4',
        ],
        ALLOWED_ATTR: ['href', 'title'],
        ALLOW_DATA_ATTR: false,
    });
}

/** Strip markdown to plain text for compact previews (PostCard lede). */
export function markdownToPlainText(src: string, maxLen = 200): string {
    if (!src) return '';
    // Cheap: strip common markdown tokens instead of round-tripping through
    // marked. Good enough for a card preview.
    const stripped = src
        .replace(/```[\s\S]*?```/g, '')      // fenced blocks
        .replace(/`([^`]+)`/g, '$1')          // inline code
        .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // images
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links → link text
        .replace(/[*_~#>]/g, '')              // emphasis + headers
        .replace(/\s+/g, ' ')
        .trim();
    if (stripped.length <= maxLen) return stripped;
    return stripped.slice(0, maxLen).trimEnd() + '…';
}
